import { createCriteriaParameters, getFetch, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";
import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils.js";
import { getPatientDisplayName } from "../shared/patientDisplay.js";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english.js";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Function for querying patients with filters, pagination, sorting etc.
 */
export const queryPatients = (props, options = {}) => {
    const path = Constants.API_PATIENTS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.patients;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying an existing patient.
 */
export const queryPatient = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_PATIENTS_ID, { patient_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.patients;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for querying patients with filters, pagination, sorting etc.
 * NB! This is only meant for patient selection pop-up.
 */
export const querySelectPatients = (props, states = {}, options = {}) => {
    const params = createCriteriaParameters(options);
    const path = Constants.API_PATIENTS + params;

    standardGetQuery(props, path, (response) => {
        setInsertArrayState(states.setData, response.body.elements);
    });
};

/**
 * Look up an exact patient match by Patient ID or complete demographics.
 */
/**
 * Patients whose names are about 75% the same, ignoring case and spaces.
 */
export const querySimilarPatients = (props, callback = null) => {
    standardGetQuery(
        props,
        Constants.API_PATIENTS_SIMILAR,
        (response) => {
            if (callback) callback(response.body);
        },
        {
            onError: () => {
                if (callback) callback(null);
            }
        }
    );
};

export const matchPatients = async (props, row = {}) => {
    const identifier = row.patient_identifier?.trim();
    let path = Constants.API_PATIENTS_MATCH;

    if (identifier) {
        path += `?patient_identifier=${encodeURIComponent(identifier)}`;
    } else if (row.first_name?.trim() && row.last_name?.trim() && row.birth_date) {
        const birthDate =
            typeof row.birth_date === "string"
                ? row.birth_date.substring(0, 10)
                : row.birth_date.format
                  ? row.birth_date.format("YYYY-MM-DD")
                  : null;

        if (!birthDate) return [];

        path += `?first_name=${encodeURIComponent(row.first_name.trim())}&last_name=${encodeURIComponent(row.last_name.trim())}&birth_date=${encodeURIComponent(birthDate)}`;
    } else {
        return [];
    }

    const response = await getFetch(path, props.user.userJWT);
    return response.ok && Array.isArray(response.body) ? response.body : [];
};

export const getExistingPatientReuseMessage = (patient) => {
    const label = patient?.display_label || getPatientDisplayName(patient);
    return `This patient is already on record: ${label}.\n\nDuplicate patient records are not allowed, so no new record will be created. Continue with the existing record?`;
};

export const getDuplicatePatientBlockedMessage = (row = {}) => {
    return row.patient_identifier?.trim()
        ? `Patient ID "${row.patient_identifier.trim()}" is already in use - no new record was created.`
        : "A patient with this name and birth date is already on record - no new record was created.";
};

/**
 * Function for creating a new patient.
 */
export const createPatient = (props, options = {}, callback = null) => {
    const path = Constants.API_PATIENTS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.patients;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
        if (callback) callback(response);
    });
};

/**
 * Function for updating a patient.
 */
export const updatePatient = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_PATIENTS_ID, { patient_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.patients;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a patient.
 */
export const approvePatient = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_PATIENTS_ID_APPROVE, { patient_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.patients;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a patient.
 */
export const approvePatients = (props, options = {}) => {
    standardPutQuery(props, Constants.API_PATIENTS_APPROVE, options.body, (response) => {
        const states = props.backend.patients;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for deleting patient(s).
 */
export const deletePatients = (props, options = {}) => {
    const path = Constants.API_PATIENTS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.patients;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        // Delete all child patients children to it from states.
        // const children = props.backend.patients;
        // const childrenIds = children.data.filter((t) => options.ids.includes(t.parent_id)).map((t) => t.id);

        // setDeleteArrayState(children.setData, childrenIds);
        // children.setRowCount(Math.max(0, children.rowCount - childrenIds.length));
        // children.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};
